import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { Box, Typography, Button, TextField } from '@mui/material';

import { primaryPillButtonSx, destructivePillButtonSx, drawerActionButtonSx } from '../../../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { opsColors } from '../../../../theme/opsTokens';

interface PassengerTypesEditorProps {
  passengerTypes: any[];
  setPassengerTypes: React.Dispatch<React.SetStateAction<any[]>>;
}

const PassengerTypesEditor: React.FC<PassengerTypesEditorProps> = ({ passengerTypes, setPassengerTypes }) => {
  const { t } = useTranslation('mapWizards');
  const totalProb = passengerTypes.reduce((sum, p) => sum + (p.probability || 0), 0);
  const error = Math.abs(totalProb - 1) > 0.0001;
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };
  const parseProbabilityInput = (raw: string | number): number => {
    if (raw === '' || raw === null || raw === undefined) return 0;
    const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
    return Number.isNaN(n) ? 0 : n / 100;
  };
  const handleChange = (idx: number, field: string, value: string | number) => {
    if (field === 'probability' && passengerTypes.length === 2) {
      const v = Math.max(0, Math.min(1, parseProbabilityInput(value)));
      setPassengerTypes((prev: any[]) => prev.map((p, i) =>
        i === idx ? { ...p, probability: v } : { ...p, probability: 1 - v }
      ));
    } else {
      setPassengerTypes((prev: any[]) => prev.map((p, i) =>
        i === idx ? { ...p, [field]: field === 'probability' ? parseProbabilityInput(value) : value } : p
      ));
    }
  };
  const addType = () => setPassengerTypes((prev: any[]) => [...prev, { passenger_type: '', processing_time: 30, probability: 0 }]);
  const removeType = (idx: number) => setPassengerTypes((prev: any[]) => prev.filter((_: any, i: number) => i !== idx));
  return (
    <Box
      sx={(theme) => ({
        mt: 1.5,
        mb: 1.25,
        p: 1.75,
        border: '1px solid',
        borderColor: 'surface.borderSubtle',
        borderRadius: 2.5,
        backgroundColor: alpha(opsColors.neutral.panel, 0.4),
        backdropFilter: 'blur(8px) saturate(110%)',
        WebkitBackdropFilter: 'blur(8px) saturate(110%)',
      })}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('generateRides.passengerTypes.title')}
      </Typography>
      {passengerTypes.map((p, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap', py: 1, borderBottom: idx < passengerTypes.length - 1 ? '1px solid' : 'none', borderColor: 'surface.borderSubtle' }}>
          <TextField
            label={t('generateRides.passengerTypes.type')}
            value={p.passenger_type}
            onChange={e => handleChange(idx, 'passenger_type', e.target.value)}
            size="small"
            sx={{ width: { xs: '100%', sm: 160 } }}
          />
          <TextField
            label={t('generateRides.passengerTypes.processingTime')}
            type="number"
            value={p.processing_time}
            onChange={e => handleChange(idx, 'processing_time', Number(e.target.value))}
            onWheel={preventWheelValueChange}
            inputProps={{ min: 0, step: 1 }}
            size="small"
            sx={{ width: { xs: '100%', sm: 170 } }}
          />
          <TextField
            label={t('generateRides.passengerTypes.probability')}
            type="number"
            value={p.probability ? Math.round(p.probability * 100) : ''}
            onChange={e => handleChange(idx, 'probability', e.target.value)}
            onWheel={preventWheelValueChange}
            inputProps={{ min: 0, max: 100, step: 1 }}
            size="small"
            sx={{ width: { xs: '100%', sm: 150 } }}
            error={error}
          />
          {idx > 0 && (
            <Button
              onClick={() => removeType(idx)}
              sx={(theme) => ({
                minWidth: 36,
                width: 36,
                ml: { xs: 0, sm: 'auto' },
                ...destructivePillButtonSx(theme),
                ...drawerActionButtonSx,
                px: 0,
              })}
            >
              <Icon name="trash" size={20} />
            </Button>
          )}
        </Box>
      ))}
      <Button
        variant="contained"
        onClick={addType}
        sx={(theme) => ({
          mt: 1.25,
          ...primaryPillButtonSx(theme),
          ...drawerActionButtonSx,
          alignSelf: 'flex-start',
        })}
      >
        {t('generateRides.passengerTypes.addType')}
      </Button>
      {error && <Typography color="error" sx={{ mt: 0.75 }}>{t('generateRides.passengerTypes.probabilityError')}</Typography>}
    </Box>
  );
};

export default PassengerTypesEditor; 
